import type { RequestDestinationIfKnown } from '@fullstory/renderer-shared';
import { urlIsBlocklisted } from '@fullstory/resource-blocklist';

export type ResourceClassification = RequestDestinationIfKnown | 'blocklisted'; // We explicitly don't want to fetch this resource.

/**
 * Resource fetch initiators that are interesting for playback, and the request
 * destinations they map to. If an initiator type doesn't appear here, we won't
 * even attempt to infer the destination; this excludes things like scripts and
 * documents up front.
 */
const initiatorDestinations: Readonly<Record<string, RequestDestinationIfKnown>> = {
  css: 'unknown', // May be just about anything.
  embed: 'unknown', // Either an image or something we won't use.
  image: 'image',
  img: 'image',
  link: 'unknown', // May be just about anything.
  object: 'unknown', // Either an image or something we won't use.
  picture: 'image',
  source: 'image',
  svg: 'unknown', // May be just about anything.
};

export type ClassificationInputs = {
  destination?: RequestDestination;
  initiatorType?: string;
};

export function classifyResource(
  url: URL,
  inputs: ClassificationInputs = {},
): ResourceClassification {
  if (urlIsBlocklisted(url)) {
    return 'blocklisted';
  }

  if (inputs.destination) {
    return inputs.destination;
  }

  if (inputs.initiatorType) {
    const destFromInitiator = initiatorDestinations[inputs.initiatorType];

    // If the initiator doesn't appear in the table, it's something we're not
    // interested in.
    if (destFromInitiator === undefined) {
      return 'unknown';
    }

    // If the initiator maps unambiguously to a destination, just return it.
    if (destFromInitiator !== 'unknown') {
      return destFromInitiator;
    }
  }

  return inferDestination(url);
}

/**
 * Try to infer the destination of a fetch based on its URL.
 * @param url - The URL of a fetched resource.
 * @returns a guess as to the destination of the fetch, or 'unknown' if guessing
 * failed or if we determined that the resource is irrelevant for playback.
 */
export function inferDestination(url: URL): RequestDestinationIfKnown {
  const pathname = url.pathname;

  // Extract the filename, to the best of our ability. Note that if
  // lastIndexOf() returns -1, this is a no-op.
  const filename = pathname.slice(pathname.lastIndexOf('/') + 1);

  // Infer if this is a stylesheet based on the extension. We allow for
  // additional extension components after the one we're looking for in this
  // and all the other cases. This allows for e.g. '.css.module'.
  if (/\.i?(css|less|sass|scss)(\.|\?|#|$)/i.test(filename)) {
    return 'style';
  }

  // Infer if this is a font based on the extension. Note that there isn't a
  // special extension for SVG fonts, so we'll misclassify them as images.
  // Fortunately they aren't common.
  if (/\.(ttf|woff2?)(\.|\?|#|$)/i.test(filename)) {
    return 'font';
  }

  // Infer if this is an image based on the extension.
  if (/\.(a?png|bmp|cur|gif|ico|jfif|p?jpe?g|pjp|svg|webp)(\.|\?|#|$)/i.test(filename)) {
    return 'image';
  }

  // Give up; this is either a type of resource we don't support, or it's named
  // strangely.
  return 'unknown';
}
